import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Shield, FileText, ChevronDown, Eye, Globe, Users, Database, UserCheck, Clock, AlertTriangle, BookOpen, Mail, Download } from 'lucide-react';
interface PrivacySection {
  id: string;
  title: string;
  icon: React.ElementType;
  content: React.ReactNode;
}
const PRIVACY_SECTIONS: PrivacySection[] = [{
  id: 'informacion',
  title: '1. Información que Recopilamos',
  icon: FileText,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5907">
        <p data-id="element-5908">
          Recopilamos diferentes tipos de información para proporcionar y
          mejorar nuestro Servicio:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5909">
          <li data-id="element-5910">
            <strong data-id="element-5911">Datos de cuenta:</strong> Nombre, dirección de correo
            electrónico y detalles de la organización.
          </li>
          <li data-id="element-5912">
            <strong data-id="element-5913">Datos de uso:</strong> Mensajes enviados al agente,
            herramientas utilizadas, timestamps y patrones de interacción.
          </li>
          <li data-id="element-5914">
            <strong data-id="element-5915">Datos de integraciones:</strong> Contenido de emails,
            eventos de calendario y archivos procesados a través de nuestro MCP
            Gateway cuando usted lo solicita explícitamente.
          </li>
          <li data-id="element-5916">
            <strong data-id="element-5917">Datos técnicos:</strong> Dirección IP, tipo de navegador e
            información del dispositivo (utilizados exclusivamente para
            diagnóstico y seguridad).
          </li>
        </ul>
      </div>
}, {
  id: 'uso',
  title: '2. Cómo Usamos su Información',
  icon: Eye,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5918">
        <p data-id="element-5919">
          Utilizamos la información recopilada para los siguientes propósitos:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5920">
          <li data-id="element-5921">Proveer, mantener y mejorar el Servicio.</li>
          <li data-id="element-5922">
            Procesar sus consultas y ejecutar acciones a través del agente de
            IA.
          </li>
          <li data-id="element-5923">
            Generar métricas de uso y procesar la facturación correspondiente.
          </li>
        </ul>
        <div className="p-4 bg-indigo-950/30 border border-indigo-900/50 rounded-lg mt-4" data-id="element-5924">
          <p className="font-medium text-indigo-300" data-id="element-5925">
            <strong data-id="element-5926">Importante:</strong> No vendemos sus datos personales a
            terceros bajo ninguna circunstancia, y no utilizamos sus datos
            privados para entrenar nuestros modelos de IA fundacionales.
          </p>
        </div>
      </div>
}, {
  id: 'integraciones',
  title: '3. Integraciones y Flujo de Datos',
  icon: Globe,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5927">
        <p data-id="element-5928">
          Nuestro Servicio se conecta con herramientas externas de la siguiente
          manera:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5929">
          <li data-id="element-5930">
            <strong data-id="element-5931">Google Workspace:</strong> La autenticación se realiza vía
            OAuth a través de nuestro MCP Gateway. Los datos fluyen de servidor
            a servidor.
          </li>
          <li data-id="element-5932">
            <strong data-id="element-5933">Jira y Confluence:</strong> Conexión a través de MCP
            utilizando autenticación Cognito.
          </li>
          <li data-id="element-5934">
            <strong data-id="element-5935">GitHub:</strong> Los tokens de acceso personal se almacenan
            cifrados localmente en su navegador (localStorage).
          </li>
          <li data-id="element-5936">
            <strong data-id="element-5937">Slack:</strong> Integración mediante webhooks y polling; los
            mensajes se procesan en tiempo real.
          </li>
        </ul>
        <p data-id="element-5938">
          Los datos provenientes de estas integraciones se procesan en memoria
          para responder a sus consultas y no se almacenan permanentemente en
          nuestros servidores, a excepción del historial de chat cifrado.
        </p>
      </div>
}, {
  id: 'seguridad',
  title: '4. Almacenamiento y Seguridad',
  icon: Shield,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5939">
        <p data-id="element-5940">
          Implementamos medidas de seguridad de nivel empresarial para proteger
          su información:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5941">
          <li data-id="element-5942">
            <strong data-id="element-5943">Infraestructura:</strong> Alojada en AWS (región us-east-1)
            con certificaciones de seguridad estándar de la industria.
          </li>
          <li data-id="element-5944">
            <strong data-id="element-5945">Gestión de Tokens:</strong> Los tokens OAuth se almacenan de
            forma segura en AWS Secrets Manager y nunca se exponen en el
            navegador del usuario.
          </li>
          <li data-id="element-5946">
            <strong data-id="element-5947">Cifrado:</strong> Todas las comunicaciones en tránsito
            utilizan TLS 1.3. El historial de chat se cifra en reposo.
          </li>
          <li data-id="element-5948">
            <strong data-id="element-5949">Control de Acceso:</strong> El acceso interno a los datos
            está estrictamente restringido mediante roles IAM y el principio de
            menor privilegio.
          </li>
        </ul>
      </div>
}, {
  id: 'terceros',
  title: '5. Compartición con Terceros',
  icon: Users,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5950">
        <p data-id="element-5951">
          Solo compartimos información con terceros esenciales para el
          funcionamiento del Servicio:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5952">
          <li data-id="element-5953">
            <strong data-id="element-5954">Proveedores de LLM (OpenAI, Anthropic):</strong> Reciben el
            contexto necesario para procesar sus consultas. Existen acuerdos
            estrictos que prohíben a estos proveedores retener sus datos o
            usarlos para entrenamiento.
          </li>
          <li data-id="element-5955">
            <strong data-id="element-5956">Proveedores de Infraestructura (AWS):</strong> Para el
            alojamiento y procesamiento seguro en la nube.
          </li>
        </ul>
        <p data-id="element-5957">
          No compartimos, vendemos ni alquilamos sus datos a anunciantes, redes
          publicitarias ni data brokers.
        </p>
      </div>
}, {
  id: 'cookies',
  title: '6. Cookies y Almacenamiento Local',
  icon: Database,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5958">
        <p data-id="element-5959">
          Nuestro enfoque respecto al almacenamiento local respeta su
          privacidad:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5960">
          <li data-id="element-5961">
            <strong data-id="element-5962">Sin Tracking:</strong> No utilizamos cookies de seguimiento
            (tracking cookies) ni píxeles invasivos.
          </li>
          <li data-id="element-5963">
            <strong data-id="element-5964">Uso Funcional:</strong> Utilizamos <code data-id="element-5965">localStorage</code>{' '}
            exclusivamente para mantener su sesión activa, guardar preferencias
            de interfaz y almacenar identificadores necesarios (como el{' '}
            <code data-id="element-5966">tenant_id</code> de MCP).
          </li>
          <li data-id="element-5967">
            <strong data-id="element-5968">Analytics:</strong> No integramos herramientas de analítica
            de terceros que rastreen su comportamiento individual a través de la
            web.
          </li>
        </ul>
      </div>
}, {
  id: 'derechos',
  title: '7. Sus Derechos',
  icon: UserCheck,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5969">
        <p data-id="element-5970">Usted tiene control total sobre sus datos personales:</p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5971">
          <li data-id="element-5972">
            <strong data-id="element-5973">Acceso:</strong> Puede solicitar una copia de todos los
            datos personales que tenemos sobre usted.
          </li>
          <li data-id="element-5974">
            <strong data-id="element-5975">Rectificación:</strong> Puede corregir datos inexactos o
            incompletos en su perfil.
          </li>
          <li data-id="element-5976">
            <strong data-id="element-5977">Eliminación (Derecho al olvido):</strong> Puede solicitar el
            borrado completo de su cuenta y todos los datos asociados.
          </li>
          <li data-id="element-5978">
            <strong data-id="element-5979">Portabilidad:</strong> Puede exportar su historial y datos
            en un formato estándar y legible por máquina.
          </li>
          <li data-id="element-5980">
            <strong data-id="element-5981">Revocación:</strong> Puede desconectar cualquier integración
            (Google, Jira, etc.) en cualquier momento desde el panel de
            Conectores, lo que eliminará inmediatamente nuestro acceso.
          </li>
        </ul>
      </div>
}, {
  id: 'retencion',
  title: '8. Retención de Datos',
  icon: Clock,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5982">
        <p data-id="element-5983">Mantenemos sus datos solo durante el tiempo necesario:</p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5984">
          <li data-id="element-5985">
            <strong data-id="element-5986">Datos de cuenta:</strong> Se conservan mientras su cuenta
            permanezca activa.
          </li>
          <li data-id="element-5987">
            <strong data-id="element-5988">Historial de chat:</strong> Se conserva hasta que usted
            decida eliminarlo manualmente desde la interfaz.
          </li>
          <li data-id="element-5989">
            <strong data-id="element-5990">Tokens OAuth:</strong> Se conservan hasta que usted
            desconecte el servicio o el token expire/sea revocado desde el
            proveedor.
          </li>
          <li data-id="element-5991">
            <strong data-id="element-5992">Logs de uso y diagnóstico:</strong> Se retienen por un
            máximo de 90 días por motivos de seguridad y depuración, luego se
            eliminan automáticamente.
          </li>
        </ul>
      </div>
}, {
  id: 'menores',
  title: '9. Menores de Edad',
  icon: AlertTriangle,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5993">
        <p data-id="element-5994">
          Nuestro Servicio está diseñado para uso profesional y empresarial, y
          no está dirigido a personas menores de 18 años.
        </p>
        <p data-id="element-5995">
          No recopilamos intencionalmente información personal de menores de
          edad. Si descubrimos que hemos recopilado datos de un menor, tomaremos
          medidas para eliminar esa información lo antes posible.
        </p>
      </div>
}, {
  id: 'cambios',
  title: '10. Cambios a esta Política',
  icon: BookOpen,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5996">
        <p data-id="element-5997">
          Podemos actualizar nuestra Política de Privacidad periódicamente para
          reflejar cambios en nuestras prácticas o por razones legales y
          regulatorias.
        </p>
        <p data-id="element-5998">
          En caso de cambios materiales, le notificaremos por correo electrónico
          o mediante un aviso destacado en la plataforma con al menos 30 días de
          anticipación. El uso continuo del Servicio después de dichos cambios
          implica su aceptación de la política actualizada.
        </p>
      </div>
}, {
  id: 'contacto',
  title: '11. Contacto del DPO',
  icon: Mail,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5999">
        <p data-id="element-6000">
          Si tiene preguntas, inquietudes o solicitudes relacionadas con esta
          Política de Privacidad o el manejo de sus datos, puede contactar a
          nuestro Oficial de Protección de Datos (DPO):
        </p>
        <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 inline-block" data-id="element-6001">
          <p className="font-mono text-indigo-400" data-id="element-6002">privacy@agent.ai</p>
        </div>
      </div>
}];
export function PrivacyPolicyPanel() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    informacion: true
  });
  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const expandAll = () => {
    const allOpen = PRIVACY_SECTIONS.reduce((acc, section) => {
      acc[section.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenSections(allOpen);
  };
  const collapseAll = () => {
    setOpenSections({});
  };
  const scrollToSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: true
    }));
    setTimeout(() => {
      const element = document.getElementById(`privacy-section-${id}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-6003">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-6004">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-6005">
          <div data-id="element-6006">
            <div className="flex items-center gap-3 mb-2" data-id="element-6007">
              <div className="p-2.5 bg-indigo-950/50 rounded-xl border border-indigo-900/50" data-id="element-6008">
                <Shield size={24} className="text-indigo-400" data-id="element-6009" />
              </div>
              <h1 className="text-3xl font-bold text-slate-100" data-id="element-6010">
                Política de Privacidad
              </h1>
            </div>
            <p className="text-slate-400 mt-2" data-id="element-6011">
              Cómo recopilamos, usamos y protegemos su información personal.
            </p>
            <div className="flex items-center gap-3 mt-4" data-id="element-6012">
              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-300" data-id="element-6013">
                Versión 1.3
              </Badge>
              <span className="text-xs text-slate-500" data-id="element-6014">
                Última actualización: 15 de Octubre, 2026
              </span>
            </div>
          </div>
          <div className="shrink-0" data-id="element-6015">
            <Button variant="outline" className="gap-2 bg-slate-900 border-slate-700 text-slate-300 hover:text-slate-100 hover:bg-slate-800" data-id="element-6016">
              <Download size={16} data-id="element-6017" />
              Descargar PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-6018">
          {/* Table of Contents (Sidebar) */}
          <div className="lg:col-span-1" data-id="element-6019">
            <div className="sticky top-8 space-y-4" data-id="element-6020">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-6021">
                Contenido
              </h3>
              <nav className="space-y-1 border-l-2 border-slate-800" data-id="element-6022">
                {PRIVACY_SECTIONS.map(section => <button key={`toc-${section.id}`} onClick={() => scrollToSection(section.id)} className={`w-full text-left px-4 py-2 text-sm transition-colors border-l-2 -ml-[2px] ${openSections[section.id] ? 'border-indigo-500 text-indigo-400 font-medium bg-indigo-950/10' : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-6023">
                    {section.title}
                  </button>)}
              </nav>

              <div className="pt-4 flex gap-2" data-id="element-6024">
                <button onClick={expandAll} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-6025">
                  Expandir todo
                </button>
                <span className="text-slate-700" data-id="element-6026">•</span>
                <button onClick={collapseAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-6027">
                  Colapsar todo
                </button>
              </div>
            </div>
          </div>

          {/* Accordion Content */}
          <div className="lg:col-span-3 space-y-4 pb-20" data-id="element-6028">
            {PRIVACY_SECTIONS.map(section => {
            const Icon = section.icon;
            const isOpen = !!openSections[section.id];
            return <Card key={section.id} id={`privacy-section-${section.id}`} className={`bg-slate-900 border-slate-800 overflow-hidden transition-colors ${isOpen ? 'border-slate-700 shadow-md' : 'hover:border-slate-700'}`} data-id="element-6029">
                  <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" data-id="element-6030">
                    <div className="flex items-center gap-3" data-id="element-6031">
                      <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-indigo-950/50 text-indigo-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-6032">
                        <Icon size={18} data-id="element-6033" />
                      </div>
                      <h2 className={`text-lg font-semibold transition-colors ${isOpen ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-6034">
                        {section.title}
                      </h2>
                    </div>
                    <ChevronDown size={20} className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-400' : ''}`} data-id="element-6035" />
                  </button>

                  <AnimatePresence initial={false} data-id="element-6036">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} data-id="element-6037">
                        <div className="px-5 pb-5 pt-2 border-t border-slate-800/50" data-id="element-6038">
                          {section.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </div>;
}